var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var TextGradeLevelConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import invariant from 'ts-invariant';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { getRenamedColumns } from './toolConfigUtil';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { buildInputsMap, validateSourceWithDetail } from './validation/validateHelper';
const GradeLevelColumn = 'Grade Level';
export var GradeMethod;
(function (GradeMethod) {
    GradeMethod["automated_readability_index"] = "automated_readability_index";
    GradeMethod["coleman_liau_index"] = "coleman_liau_index";
    GradeMethod["dale_chall_readability_score"] = "dale_chall_readability_score";
    GradeMethod["flesch_kincaid_grade"] = "flesch_kincaid_grade";
    GradeMethod["gunning_fog"] = "gunning_fog";
    GradeMethod["linsear_write_formula"] = "linsear_write_formula";
    GradeMethod["smog_index"] = "smog_index";
})(GradeMethod || (GradeMethod = {}));
const schema = {
    $id: 'urn:wisepipe-common-pipe:TextGradeLevelConfig',
    type: 'object',
    properties: {
        targetColumn: { type: 'string', minLength: 1, maxLength: 150 },
        extras: {
            type: 'array',
            items: {
                type: 'string',
                enum: Object.values(GradeMethod)
            },
            uniqueItems: true
        }
    },
    required: ['targetColumn', 'extras']
};
let TextGradeLevelConfig = TextGradeLevelConfig_1 = class TextGradeLevelConfig extends ToolConfig {
    constructor(targetColumn, extras, customFormats) {
        super(ToolType.TEXT_GRADE_LEVEL, customFormats);
        this.targetColumn = targetColumn;
        this.extras = extras === undefined ? [] : extras.sort();
        JSON.validate(this, TextGradeLevelConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return TextGradeLevelConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return TextGradeLevelConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return TextGradeLevelConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return TextGradeLevelConfig_1.outs;
    }
    static get in() {
        return TextGradeLevelConfig_1.ins[0];
    }
    static get out() {
        return TextGradeLevelConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.TEXT_GRADE_LEVEL.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.LEARN], this.ins, this.outs, ['cnn', 'neural network', 'bow', 'bag-of-words', 'ngram'], false);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        invariant(source, `expected source to be defined, got ${JSON.stringify(source)}`);
        const exp = tsb.export(this.out);
        const dependents = this.copyAllInsAndReturnDependents(exp, this.in, source, [this.targetColumn]);
        const columns = source.map((col) => col.name);
        const gradeLevelColumnName = getRenamedColumns(columns, GradeLevelColumn, this.toolType);
        exp.makeNew(new Column(gradeLevelColumnName, 'number'), dependents, true);
        // add extra columns, if any
        const extras = this.extras.map((x) => TextGradeLevelConfig_1.gradeMethodName(x));
        extras.forEach((name) => {
            const extraName = getRenamedColumns(columns, name, this.toolType);
            exp.makeNew(new Column(extraName, 'number'), dependents, true);
        });
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    static gradeMethodName(method) {
        const text = GradeMethod[method];
        const spaces = text.replace(/_/g, ' ');
        const titleCase = spaces
            .split(' ')
            .filter((word) => word.length > 0)
            .map((word) => word.replace(word[0], word[0].toUpperCase()))
            .join(' ');
        return titleCase;
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const inputs = buildInputsMap([this.targetColumn], ['string']);
        const columns = source.map((col) => col.name);
        const gradeLevelColumnName = getRenamedColumns(columns, GradeLevelColumn, this.toolType);
        const outputs = [gradeLevelColumnName].concat(this.extras.map((x) => {
            const gradeName = TextGradeLevelConfig_1.gradeMethodName(x);
            const newNam = getRenamedColumns(columns, gradeName, this.toolType);
            return newNam;
        }));
        const sourceValidationResult = validateSourceWithDetail(source, inputs, outputs);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
TextGradeLevelConfig = TextGradeLevelConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.TEXT_GRADE_LEVEL)
], TextGradeLevelConfig);
export { TextGradeLevelConfig };
//# sourceMappingURL=textGradeLevelConfig.js.map